import TimeTracker from '@/components/TimeTracker'
import TimeEntriesList from '@/components/TimeEntriesList'
import ProjectHoursChart from '@/components/ProjectHoursChart'
import { useProjects } from '@/hooks/useProjects'
import { useTimeTrackingContext } from '@/hooks/useTimeTrackingContext'
import { useTimeEntriesCache } from '@/hooks/useTimeEntriesCache'
import { useUserSettingsContext } from '@/hooks/useUserSettingsContext'
import { useMemo } from 'react';
import { detectOverlappingEntries } from '@/utils/overlapDetection';
import { computeBudgetViolatingEntryIds } from '@/utils/budgetUtils';

export default function Dashboard() {
  const timeTracking = useTimeTrackingContext()
  const projects = useProjects()
  const { allEntries, loading: cacheLoading, loadProgress } = useTimeEntriesCache()
  const { displaySettings, timezoneSettings, effectiveWeekStartDay } = useUserSettingsContext()

  // Memoize the projects array to prevent unnecessary re-renders
  const allProjects = useMemo(() => projects.allProjects || [], [projects.allProjects]);

  // Only active projects — used for creation dropdowns / charts
  // (allProjects only contains active projects — archived ones are not loaded here)
  const activeProjects = useMemo(
    () => allProjects.filter(p => p.status === 'active' || !p.status),
    [allProjects]
  );

  // Entries filtered to active projects only — guard against loading flash with projects.loading
  const activeEntries = useMemo(() => {
    if (projects.loading) return allEntries;
    const activeProjectIds = new Set(activeProjects.map(p => p.id));
    return allEntries.filter(e => !e.projectId || activeProjectIds.has(e.projectId));
  }, [allEntries, activeProjects, projects.loading]);

  // Filter to completed entries for the chart
  const completedEntries = useMemo(() =>
    activeEntries.filter(entry => entry.endTime),
    [activeEntries]
  );

  // Get recent entries for the list (most recent first)
  const recentEntries = useMemo(() =>
    [...activeEntries].sort((a, b) =>
      new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    ),
    [activeEntries]
  );

  // Detect overlapping entries within the same project using shared utility
  // Uses "benefit of doubt" approach: minute precision for form entries, second precision for timer entries
  const overlappingEntryIds = useMemo(() => {
    return detectOverlappingEntries(activeEntries);
  }, [activeEntries]);

  // Compute budget-violating entry IDs for the recent entries list
  const budgetViolatingEntryIds = useMemo(() => {
    if (!displaySettings.showBudgetWarnings) return new Set<string>();
    return computeBudgetViolatingEntryIds(activeEntries, activeProjects, timeTracking.getCurrentDuration);
  }, [activeEntries, activeProjects, displaySettings.showBudgetWarnings, timeTracking.getCurrentDuration]);

  if (projects.loading || cacheLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          {cacheLoading && loadProgress.estimated > 0 && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-2">
                Loading time entries: {loadProgress.loaded} / {loadProgress.estimated}
              </p>
              <div className="w-64 mx-auto bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(loadProgress.loaded / loadProgress.estimated) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <TimeTracker {...timeTracking} projects={activeProjects} preferredTimezone={timezoneSettings.preferredTimezone} weekStartsOn={effectiveWeekStartDay} showActiveEntryAnimations={displaySettings.showActiveEntryAnimations} />
          <ProjectHoursChart
            timeEntries={completedEntries}
            projects={activeProjects}
            showBudgetWarnings={displaySettings.showBudgetWarnings}
            budgetWarningColor={displaySettings.budgetWarningColor}
            weekStartsOn={effectiveWeekStartDay}
          />
        </div>
        <TimeEntriesList
          timeEntries={recentEntries}
          runningEntry={timeTracking.runningEntry}
          loading={cacheLoading}
          formatDuration={timeTracking.formatDuration}
          getCurrentDuration={timeTracking.getCurrentDuration}
          deleteTimeEntry={timeTracking.deleteTimeEntry}
          updateTimeEntry={timeTracking.updateTimeEntry}
          resumeTimeEntry={timeTracking.resumeTimeEntry}
          projects={allProjects}
          showTimezoneIndicator={displaySettings.showTimezoneIndicator}
          showOverlapIndicator={displaySettings.showOverlapIndicator}
          overlapIndicatorColor={displaySettings.overlapIndicatorColor}
          overlappingEntryIds={overlappingEntryIds}
          showBudgetWarnings={displaySettings.showBudgetWarnings}
          budgetWarningColor={displaySettings.budgetWarningColor}
          budgetViolatingEntryIds={budgetViolatingEntryIds}
          preferredTimezone={timezoneSettings.preferredTimezone}
          weekStartsOn={effectiveWeekStartDay}
        />
      </div>
    </div>
  )
} 